import { css, cx } from '@linaria/core'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { HoverTooltip } from '#components/HoverTooltip'
import { Icon } from '#components/Icon'
import { toaster } from '#features/Toasts/toaster'
import FlatLogoIcon from '#icons/man-logo-flat.svg'
import { fragments, style } from '#styles'
import { sharedTestIds } from '#utils/sharedTestIds'
import { PageHeaderSlots } from './PageHeaderSlots'
import { usePageHeaderContext } from './usePageHeaderContext'

const navClass = css`
  z-index: 1;
  isolation: isolate;
  position: sticky;
  top: 0;
  height: ${style.constants.headerHeightVariable};
  background: ${style.theme.backgroundSurfacePrimary};
  /* border-bottom: 1px solid ${style.theme.borderSurface}; */
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;

  &:after {
    z-index: -1;
    content: ' ';
    position: absolute;
    top: auto;
    right: 0;
    bottom: 0px;
    left: 0;
    height: 1px;
    background-color: ${style.theme.borderSurface};
    transition: ease 0.25s all;
  }

  .content-area {
    .nav-area {
      display: flex;
      align-items: stretch;
      padding-right: 16px;
    }
  }

  > .logo-container {
    height: 40px;
    display: flex;
    align-items: flex-end;
    gap: 20px;
    padding: 0 0 16px 24px;

    > .logo {
      cursor: pointer;
      height: 24px;
      width: 44px;
    }

    > .product-title {
      color: ${style.theme.textSoft};
      ${fragments.textStyle.body.s.regular}
      line-height: 120%;
    }
  }

  > .separator {
    margin: 12px 20px;
    height: 32px;
    width: 1px;
    background-color: ${style.theme.borderSurface};
  }

  @media print {
    display: none;
  }
`

const headersClass = css`
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0px;
  /* disallow pushing the layout out of bounds */
  overflow: hidden;
`

const navEntriesGroupClass = css`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-end;
  flex-wrap: nowrap;
`

export const PageHeader: React.FC<{ appName: string; onLogoClick: () => void }> = ({ appName, onLogoClick }) => {
  const { setBaseRoot } = usePageHeaderContext()
  const { t } = useTranslation('shared')
  return (
    <nav className={navClass}>
      <div className="logo-container">
        <Icon className="logo" iconUrl={FlatLogoIcon} data-testid={sharedTestIds.layout.headerLogo} onClick={onLogoClick} />
        <HoverTooltip.span
          className="logo-container"
          title={`Release v${VITE_APP_VERSION} - ${VITE_BUILD_TIME}`}
          offset={10}
          onClick={() => {
            void navigator.clipboard.writeText(`${appName} Release: v${VITE_APP_VERSION} sha:${VITE_COMMIT_SHA} at ${VITE_BUILD_TIME}`)
            toaster.add({ title: t('common.toasts.copied-to-clipboard', 'Copied to clipboard') })
          }}
        >
          {appName}
        </HoverTooltip.span>
      </div>
      <div className="separator" />

      <div className={cx('content-area', headersClass)}>
        <div className={cx('nav-area', navEntriesGroupClass)} ref={setBaseRoot}>
          <PageHeaderSlots />
        </div>
      </div>
    </nav>
  )
}
